import JsonResponse from "csi/comm/JsonResponse";
import { createFormData, postFormData, REQUEST_MODULE, REQUEST_OPERATION } from "../core/request";
import { DataStoreId, DataStorePrefixes, IContextualDataStoreId, IDataStore, IDataStoreId, IDataStoreInfo, IDataStoreNamesResponse, IDataStoreParams, IGlobalDataStoreId, IRelationDataStoreId, ISubtypeDataStoreId } from "../type/dataStore";
import { C8AttributeId, C8NodeType } from "../type/node";
import { cnl } from "../type/value";
import { IRequestHandlerResponse } from "../type/requestHandlerResponse";

const targetExpression = /\(T\)([^(]+)/;
const otherPartsExpression = /\(P\)(.*)/;
const dataStoreTargetClass = "(T)";
const dataStorePrefix = "(P)";
const customStorePrefix = "(C)";
const slash = "/";

function constructNamedDataStoreId<T>(namePart: string, otherParams: Partial<T>): T {
	const index = namePart?.indexOf(customStorePrefix);
	if (index === 0) {
		return {
			custom: true,
			name: namePart.substring(index + customStorePrefix.length),
			...otherParams,
		} as T;
	}

	return {
		name: namePart,
		...otherParams,
	} as T;
}

/** return the data store components from data store id */
export function getDataStoreComponents(dataStoreId: DataStoreId): IDataStoreId | undefined {
	if (!dataStoreId) {
		return;
	}

	try {
		// When we use regular expression to match with the data store id, an array with two elements returns (T)Style at index 0 and Style at index 1.
		// We only want Style, which we are using index 1.
		const target = dataStoreId.match(targetExpression)[1];
		const otherParts = dataStoreId.match(otherPartsExpression)[1];
		const parts = otherParts.split("/");
		const prefix = parts[0];
		let dataStoreComponent: IDataStoreId;

		if (prefix === DataStorePrefixes.ALL) {
			dataStoreComponent = constructNamedDataStoreId<IGlobalDataStoreId>(parts[1], {
				prefix,
				target,
			});
		} else if (prefix === DataStorePrefixes.SOURCE) {
			dataStoreComponent = constructNamedDataStoreId<IContextualDataStoreId>(parts[2], {
				prefix,
				sourceClass: parts[1],
				target,
			});
		} else if (prefix === DataStorePrefixes.RELATION) {
			dataStoreComponent = {
				prefix,
				relation: parts[2],
				sourceClass: parts[1],
				target,
			} as IRelationDataStoreId;
		} else {
			dataStoreComponent = {
				onlyActiveSubtypes: parts[1] === "active",
				prefix,
				subtypedClass: parts[2],
				target,
			} as ISubtypeDataStoreId;
		}
		return dataStoreComponent;
	} catch {
		console.error("Error occurred while trying to get data store components from data store id: " + dataStoreId);   
	}
}

/** construct data store id from data store components */
export function constructDataStoreIdFromComponents(components: IDataStoreId): DataStoreId {
	const { prefix, relation, target } = components as IRelationDataStoreId;
	const { custom, name, sourceClass } = components as IContextualDataStoreId;
	const { onlyActiveSubtypes, subtypedClass } = components as ISubtypeDataStoreId;
	
	if (subtypedClass) {
		// subtype Data Stores
		const allOrActiveSubtypes = onlyActiveSubtypes ? "active" : "all";
		return `${dataStoreTargetClass}${target}${dataStorePrefix}${DataStorePrefixes.SUBTYPE}${slash}${allOrActiveSubtypes}${slash}${subtypedClass}`;
	} else if (relation) {
		// Relation Data Stores
		return `${dataStoreTargetClass}${target}${dataStorePrefix}${DataStorePrefixes.RELATION}${slash}${sourceClass}${slash}${relation}`;
	}

	const namePart = custom ? `${customStorePrefix}${name}` : name;

	if (prefix === DataStorePrefixes.ALL) {
		// Global Data Stores
		const suffix = namePart ? `${slash}${namePart}` : "";
		return `${dataStoreTargetClass}${target}${dataStorePrefix}${DataStorePrefixes.ALL}${suffix}`;
	}

	// Contextual Data Stores
	return `${dataStoreTargetClass}${target}${dataStorePrefix}${DataStorePrefixes.SOURCE}${slash}${sourceClass}${slash}${namePart}`;
}

/** return contextual data store id for given parameters */
export function contextualDataStoreId(sourceClass: C8NodeType, name: string, target: C8NodeType, custom?: boolean): DataStoreId {
	return constructDataStoreIdFromComponents({
		custom,
		name,
		prefix: DataStorePrefixes.SOURCE,
		sourceClass,
		target,
	} as IGlobalDataStoreId);
}

/** return global data store id for given parameters */
export function globalDataStoreId(target: C8NodeType, name?: string, custom?: boolean): DataStoreId {
	return constructDataStoreIdFromComponents({
		custom,
		name,
		prefix: DataStorePrefixes.ALL,
		target,
	} as IGlobalDataStoreId);
}

/** construct relation data store id for given parameters */
export function relationDataStoreId(sourceClass: C8NodeType, relation: C8AttributeId, target: C8NodeType): DataStoreId {
	return constructDataStoreIdFromComponents({
		prefix: DataStorePrefixes.RELATION,
		relation,
		sourceClass,
		target,
	} as IRelationDataStoreId);
}

/** return true if given data store id is global */
export function isGlobalDataStore(dataStoreId: DataStoreId): boolean {
	const components = getDataStoreComponents(dataStoreId);
	return components?.prefix === DataStorePrefixes.ALL; 
}

/** query data store information for given data store id and optional source class url */
export async function queryDataStoreInfo(dataStoreId: DataStoreId, sourceCnl?: cnl): Promise<IDataStoreInfo> {
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetDataStoreInfo",
		DataStoreId: dataStoreId,
		SourceUrl: sourceCnl,
	});

	const result = await postFormData<IDataStoreInfo>(formData);
	return result;
}

/** query name/url information from the given data store */
export async function queryDataStoreNames(params: IDataStoreParams): Promise<IDataStoreNamesResponse> {
	const { dataStoreId, queryXml, sourceUrl } = params;
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetNames",
		DataStoreId: dataStoreId,
		SourceUrl: sourceUrl,
		"Qry.XML": queryXml,
	});

	const result = await postFormData<IDataStoreNamesResponse>(formData);
	return result;
}

/** query nodes from the given data store */
export async function queryDataStoreNodes(params: IDataStoreParams): Promise<IRequestHandlerResponse> {
	const { dataStoreId, queryXml, sourceUrl } = params;
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetNodes",
		DataStoreId: dataStoreId,
		SourceUrl: sourceUrl,
		"Qry.XML": queryXml,
	});

	// TODO: Need to add (many) more parameters to support paging, etc.

	const result = await postFormData<IRequestHandlerResponse>(formData);
	return result;
}

/** create form data for data store values */
export function createDataStoreValuesFormData(dataStoreId: DataStoreId, sourceCnl?: cnl): FormData {
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetValues",
		DataStoreId: dataStoreId,
		SourceUrl: sourceCnl,
	});
	return formData;
}

/** query data store values for given data store id and optional source class url */
export async function queryDataStoreValues(dataStoreId: DataStoreId, sourceCnl?: cnl): Promise<IRequestHandlerResponse> {
	const formData = createDataStoreValuesFormData(dataStoreId, sourceCnl);
	const result = await postFormData<IRequestHandlerResponse>(formData);
	return result;
}

/** query data store values for a given form data */
export async function queryDataStoreValuesForFormData(formData: FormData): Promise<IRequestHandlerResponse> {
	const result = await postFormData<IRequestHandlerResponse>(formData);
	return result;
}

/** query data stores for given source types and target type */
export async function queryDataStores(
	targetType: C8NodeType,
	sourceTypes?: C8NodeType | C8NodeType[],
	includeGlobal?: boolean,
	pluralOnly?: boolean,
): Promise<IDataStore[]> {
	const formData = createFormData({
		[REQUEST_MODULE]: "DataStore",
		[REQUEST_OPERATION]: "GetDataStores",
		AddGlobalDataStores: !!includeGlobal,
		OnlyPluralRelationalDataStores: !!pluralOnly,
		SourceClass: sourceTypes,
		TargetClass: targetType,
	});

	const result = await postFormData<IDataStore[]>(formData);
	return result;
}
